import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Container, Slider, Typography, Grid,
  Input, IconButton, Stack, Button, Chip, Snackbar, Alert
} from '@mui/material';
import OpacityIcon from '@mui/icons-material/Opacity';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import StarIcon from '@mui/icons-material/Star';
import LockIcon from '@mui/icons-material/Lock';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
// import './AppPage.css'; // We'll remove most of this later
import AdsenseAd from './AdsenseAd'; // Import the new component
import ProUpgradeModal from './ProUpgradeModal';
import useEntitlement from '../useEntitlement';

function AppPage() {
  // Pro entitlement (Stripe). Drives feature gating throughout the editor.
  const {
    features, isPro, justUpgraded, dismissUpgraded, startCheckout,
  } = useEntitlement();
  const [showProModal, setShowProModal] = useState(false);
  const customSilInputRef = useRef(null);

  const [backgroundImage, setBackgroundImage] = useState(null);
  const [silhouetteImage, setSilhouetteImage] = useState(null);
  const [loadedSilhouette, setLoadedSilhouette] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [silhouetteScale, setSilhouetteScale] = useState(1);
  const [silhouetteOpacity, setSilhouetteOpacity] = useState(1);
  const [silhouetteOffsetX, setSilhouetteOffsetX] = useState(0);
  const [silhouetteOffsetY, setSilhouetteOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [initialDragOffsets, setInitialDragOffsets] = useState({ x: 0, y: 0 });

  // Carousel state
  const [currentSilhouetteStartIndex, setCurrentSilhouetteStartIndex] = useState(0);
  const silhouettesPerPage = 3; // Show 3 silhouettes at a time in the carousel

  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBackgroundImage(e.target.result);
        setSilhouetteImage(null);
        setLoadedSilhouette(null);
        setSilhouetteOffsetX(0);
        setSilhouetteOffsetY(0);
        if (isCameraActive) closeCamera();
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSilhouetteSelect = (sil) => {
    // Pro-gated silhouettes open the upgrade modal instead of selecting.
    if (sil.pro && !features.allSilhouettes) {
      setShowProModal(true);
      return;
    }
    setSilhouetteImage(sil.src);
    setSilhouetteOffsetX(0);
    setSilhouetteOffsetY(0);
    // Optional: If a silhouette is selected, maybe reset carousel to its page?
    // For now, selection doesn't change carousel position.
  };

  // Custom silhouette upload (Pro-only). Loads a user image as the silhouette.
  const handleCustomSilhouetteClick = () => {
    if (!features.customUpload) {
      setShowProModal(true);
      return;
    }
    if (customSilInputRef.current) customSilInputRef.current.click();
  };

  const handleCustomSilhouetteUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSilhouetteImage(e.target.result);
        setSilhouetteOffsetX(0);
        setSilhouetteOffsetY(0);
      };
      reader.readAsDataURL(file);
    }
    event.target.value = ''; // allow re-selecting the same file
  };

  const openCamera = async () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        setCameraStream(stream);
        setIsCameraActive(true);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Error accessing camera: ", err);
        alert("Could not access the camera. Please ensure permissions are granted and check the console for more details.");
      }
    } else {
      alert("Your browser does not support camera access.");
    }
  };

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    setCameraStream(null);
    setIsCameraActive(false);
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
      const dataUrl = canvas.toDataURL('image/png');
      setBackgroundImage(dataUrl);
      setSilhouetteImage(null);
      setLoadedSilhouette(null);
      setSilhouetteOffsetX(0);
      setSilhouetteOffsetY(0);
      closeCamera();
    }
  };

  // Composite the background + silhouette to an offscreen canvas at an arbitrary
  // scale factor. Pro (hdExport) renders at 2x with no watermark; free renders
  // at 1x with the nutscaper.com watermark.
  const composeExportCanvas = (scaleFactor, withWatermark) =>
    new Promise((resolve, reject) => {
      const bgImg = new Image();
      bgImg.onload = () => {
        const off = document.createElement('canvas');
        off.width = bgImg.naturalWidth * scaleFactor;
        off.height = bgImg.naturalHeight * scaleFactor;
        const ctx = off.getContext('2d');
        ctx.drawImage(bgImg, 0, 0, off.width, off.height);

        if (loadedSilhouette && loadedSilhouette.naturalWidth > 0) {
          const scaledWidth = loadedSilhouette.naturalWidth * silhouetteScale * scaleFactor;
          const scaledHeight = loadedSilhouette.naturalHeight * silhouetteScale * scaleFactor;
          const x = (off.width - scaledWidth) / 2 + silhouetteOffsetX * scaleFactor;
          const y = silhouetteOffsetY * scaleFactor;
          ctx.globalAlpha = silhouetteOpacity;
          ctx.drawImage(loadedSilhouette, x, y, scaledWidth, scaledHeight);
          ctx.globalAlpha = 1.0;
        }

        if (withWatermark) {
          const padding = 15 * scaleFactor;
          ctx.font = `bold ${16 * scaleFactor}px Arial`;
          ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
          ctx.textAlign = 'right';
          ctx.textBaseline = 'bottom';
          ctx.fillText('nutscaper.com', off.width - padding, off.height - padding);
        }
        resolve(off);
      };
      bgImg.onerror = reject;
      bgImg.src = backgroundImage;
    });

  const handleDownloadImage = async () => {
    if (!backgroundImage) return;
    const hd = features.hdExport;
    try {
      const off = await composeExportCanvas(hd ? 2 : 1, !hd);
      const imageURL = off.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imageURL;
      link.download = hd ? 'nutscaped-image-hd.png' : 'nutscaped-image.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  useEffect(() => {
    if (isCameraActive && videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [isCameraActive, cameraStream]);

  useEffect(() => {
    if (silhouetteImage) {
      const silImgObj = new Image();
      silImgObj.crossOrigin = "Anonymous";
      silImgObj.onload = () => {
        setLoadedSilhouette(silImgObj);
      };
      silImgObj.onerror = () => {
        console.error(`Error loading silhouette image: ${silhouetteImage}`);
        setLoadedSilhouette(null);
      };
      silImgObj.src = silhouetteImage;
    } else {
      setLoadedSilhouette(null);
    }
  }, [silhouetteImage]);

  // Effect to open camera on page load
  useEffect(() => {
    openCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once on mount

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    if (!backgroundImage) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = canvas.width || 500; 
      canvas.height = canvas.height || 300;
      
      return;
    }

    const bgImg = new Image();
    bgImg.onload = () => {
      canvas.width = bgImg.naturalWidth;
      canvas.height = bgImg.naturalHeight;
      ctx.drawImage(bgImg, 0, 0);

      if (loadedSilhouette && loadedSilhouette.naturalWidth > 0) {
        const scaledWidth = loadedSilhouette.naturalWidth * silhouetteScale;
        const scaledHeight = loadedSilhouette.naturalHeight * silhouetteScale;
        const x = (canvas.width - scaledWidth) / 2 + silhouetteOffsetX;
        const y = silhouetteOffsetY;

        ctx.globalAlpha = silhouetteOpacity;
        ctx.drawImage(loadedSilhouette, x, y, scaledWidth, scaledHeight);
        ctx.globalAlpha = 1.0;

        // Draw watermark if backgroundImage is present, regardless of silhouette
        const watermarkText = "nutscaper.com";
        const padding = 15; // Pixels from edge
        ctx.font = "bold 16px Arial"; 
        ctx.fillStyle = "rgba(255, 255, 255, 0.5)"; 
        ctx.textAlign = "right";
        ctx.textBaseline = "bottom";
        ctx.fillText(watermarkText, canvas.width - padding, canvas.height - padding);
      }
    };
    bgImg.onerror = () => {
      console.error(`Error loading background image: ${backgroundImage}`);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
    bgImg.src = backgroundImage;
  }, [backgroundImage, loadedSilhouette, silhouetteScale, silhouetteOpacity, silhouetteOffsetX, silhouetteOffsetY]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const getMousePos = (canvas, evt) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (evt.clientX - rect.left) * scaleX,
      y: (evt.clientY - rect.top) * scaleY
    };
  };

  const isPointInSilhouette = (pointX, pointY) => {
    const canvas = canvasRef.current;
    if (!loadedSilhouette || !canvas || loadedSilhouette.naturalWidth === 0) return false;

    const scaledWidth = loadedSilhouette.naturalWidth * silhouetteScale;
    const scaledHeight = loadedSilhouette.naturalHeight * silhouetteScale;
    const silX = (canvas.width - scaledWidth) / 2 + silhouetteOffsetX;
    const silY = silhouetteOffsetY;

    return (
      pointX >= silX &&
      pointX <= silX + scaledWidth &&
      pointY >= silY &&
      pointY <= silY + scaledHeight
    );
  };

  const handleCanvasMouseDown = (event) => {
    if (!loadedSilhouette || !canvasRef.current) return;
    const pos = getMousePos(canvasRef.current, event);
    if (isPointInSilhouette(pos.x, pos.y)) {
      setIsDragging(true);
      setDragStartPos(pos);
      setInitialDragOffsets({ x: silhouetteOffsetX, y: silhouetteOffsetY });
      event.preventDefault();
    }
  };

  const handleCanvasMouseMove = (event) => {
    if (!isDragging || !loadedSilhouette) return;
    const pos = getMousePos(canvasRef.current, event);
    const deltaX = pos.x - dragStartPos.x;
    const deltaY = pos.y - dragStartPos.y;
    setSilhouetteOffsetX(initialDragOffsets.x + deltaX);
    setSilhouetteOffsetY(initialDragOffsets.y + deltaY);
  };

  const handleCanvasMouseUp = () => setIsDragging(false);
  const handleCanvasMouseLeave = () => setIsDragging(false);

  const handleCanvasAreaClick = () => {
    if (!backgroundImage && !isCameraActive && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleNextSilhouettes = () => {
    setCurrentSilhouetteStartIndex(prevIndex => 
      Math.min(prevIndex + silhouettesPerPage, silhouettes.length - silhouettesPerPage)
    );
  };

  const handlePrevSilhouettes = () => {
    setCurrentSilhouetteStartIndex(prevIndex => Math.max(prevIndex - silhouettesPerPage, 0));
  };

  // First 3 are free; the rest are Pro-gated. (Additional silhouette artwork is
  // tracked as a design follow-up; Pro entries currently reuse existing assets.)
  const silhouettes = [
    { name: 'Silhouette 1', src: '/img/silhouette1.png', pro: false },
    { name: 'Silhouette 2', src: '/img/silhouette2.png', pro: false },
    { name: 'Silhouette 3', src: '/img/silhouette3.png', pro: false },
    { name: 'Silhouette 4', src: '/img/silhouette1.png', pro: true },
    { name: 'Silhouette 5', src: '/img/silhouette2.png', pro: true },
    { name: 'Silhouette 6', src: '/img/silhouette3.png', pro: true },
  ];

  return (
    <Container 
      maxWidth="lg" 
      sx={{
        py: 3, 
        textAlign: 'center',
        minHeight: '100vh', // Ensure gradient covers full height
        background: 'linear-gradient(to bottom right, #FFC0CB, #FFA07A)', // Pink to light salmon gradient
        display: 'flex', // ADDED for flex layout
        flexDirection: 'column', // ADDED for flex layout
        position: 'relative', // For the Pro CTA overlay
      }}
    >
      {/* Pro upgrade CTA (or Pro badge once upgraded) */}
      {isPro ? (
        <Chip
          icon={<StarIcon />}
          label="Pro"
          color="warning"
          sx={{ position: 'absolute', top: 12, right: 12, zIndex: 40, fontWeight: 'bold' }}
        />
      ) : (
        <Button
          variant="contained"
          startIcon={<StarIcon />}
          onClick={() => setShowProModal(true)}
          sx={{
            position: 'absolute', top: 12, right: 12, zIndex: 40,
            borderRadius: '20px', textTransform: 'none', fontWeight: 'bold',
            background: 'linear-gradient(45deg, #FF8A00, #FFB300)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            '&:hover': { background: 'linear-gradient(45deg, #FF7A00, #FFA300)' },
          }}
        >
          Go Pro
        </Button>
      )}

      {/* Hidden input for custom (Pro) silhouette upload */}
      <Input
        type="file"
        accept="image/*"
        onChange={handleCustomSilhouetteUpload}
        inputRef={customSilInputRef}
        sx={{ display: 'none' }}
      />

      <Box sx={{ // This is the ControlsBox
        p: 2, 
        mb: (isCameraActive || backgroundImage) ? 0 : 3, // Adjust margin if camera/image is full area
        flexShrink: 0, // Header part should not shrink
        ...(isCameraActive && { // When camera is active, this box should grow
          flexGrow: 1,
          display: 'flex', // To allow CameraViewBox to take full height
          flexDirection: 'column', // To allow CameraViewBox to take full height
          p: 0, // Remove padding when camera is full area
          mb: 0, // Remove margin when camera is full area
        })
      }}>  
        <Input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          inputRef={fileInputRef}
          sx={{ display: 'none' }}
        />
        {!isCameraActive ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
            {/* Camera open icon used to be here, now camera opens on load */}
            {/* This stack is now empty if camera opens on load and there are no other initial controls. Could be removed or used for error states. */}
            {/* For now, if no backgroundImage and camera not active, the canvas click handles upload. This section could show a message if camera fails to load. */}
          </Stack>
        ) : (
          // New structure for camera view with overlay button
          <Box sx={{ // This is the CameraViewBox
            // REVERTED from fixed positioning, now fills parent (ControlsBox)
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', // Re-add alignItems for content within
            justifyContent: 'center', // Re-add justifyContent for content within
            position: 'relative', // For positioning overlays
            backgroundColor: 'black', // Background for video
            // gap: 2, // This might be needed if direct children need spacing
            // mx: 'auto' // No longer needed if width is 100%
            // zIndex: 10, // Only needed for fixed
            // p:0 // Padding is handled by parent or self if needed
          }}>
            <Box
              component="video"
              ref={videoRef}
              autoPlay
              playsInline
              sx={{
                width: '100%', 
                height: '100%', 
                objectFit: 'cover', // Video covers the available space
                display: 'block',
                // Removed border, borderRadius from previous fixed screen attempt
                // backgroundColor: 'black', // Handled by parent Box now
              }}
            />
            <Stack 
              direction="row" 
              spacing={2} 
              justifyContent="center"
              sx={{
                position: 'absolute',
                bottom: '20px', 
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10 
              }}
            >
              <IconButton 
                color="primary" 
                aria-label="capture photo" 
                onClick={handleCapturePhoto} 
                sx={{ 
                  height: 56, 
                  width: 56, 
                  backgroundColor: 'rgba(0,0,0,0.3)', 
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)'}
                }}
              >
                <img src="/img/camera.png" alt="Capture" style={{ width: 32, height: 32 }} />
              </IconButton>
            </Stack>

            {/* Watermark for live camera feed */}
            {isCameraActive && (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: '15px',
                  right: '15px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  zIndex: 15,
                  fontFamily: 'Arial',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.7)'
                }}
              >
                nutscaper.com
              </Typography>
            )}

            {/* Overlay for Upload Button (shown when camera is active, moved here) */}
            <IconButton
              aria-label="upload image"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              sx={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                zIndex: 20, 
                backgroundColor: 'rgba(0,0,0,0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.3)'
                },
                p: '8px'
              }}
            >
              <img src="/img/Import.png" alt="Import" style={{ width: 24, height: 24 }} />
            </IconButton>

          </Box>
        )}
      </Box>

      {/* Canvas area needs to be relative for overlay controls */}
      <Box // This is CanvasAreaBox
        sx={backgroundImage && !isCameraActive ? {
          flexGrow: 1, // ADDED: Make this box fill available space
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%', // Take full width of parent
          backgroundColor: 'black', // Background for letterboxing
          position: 'relative', // Keep for overlays
          p:0, // Remove padding when image is shown full area
          // REMOVED fixed positioning and zIndex from previous attempt
        } : {
          // Original styles for upload prompt
          mt: backgroundImage ? 2 : 0, 
          p: 0.5, 
          border: '1px dashed', 
          borderColor: 'divider', 
          borderRadius: 1, 
          display: 'inline-block', 
          lineHeight: 0, 
          cursor: !backgroundImage && !isCameraActive ? 'pointer' : 'default',
          position: 'relative' 
        }}
        onClick={!backgroundImage && !isCameraActive ? handleCanvasAreaClick : undefined}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseLeave}
          style={backgroundImage && !isCameraActive ? {
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block'
          } : {
            // Original styles for upload prompt canvas
            cursor: isDragging ? 'grabbing' : (loadedSilhouette && loadedSilhouette.naturalWidth > 0 ? 'grab' : (!backgroundImage && !isCameraActive ? 'pointer' : 'default')),
            maxWidth: '100%',
            maxHeight: '70vh',
            display: 'block' 
          }}
        />

        {/* Overlay for Scale and Opacity Controls */}
        {!isCameraActive && backgroundImage && (
          <Box 
            sx={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              p: 1.5, // Increased padding
              backgroundColor: 'rgba(0, 0, 0, 0.1)', // Slightly darker overlay
              borderRadius: '8px',
              zIndex: 20, // Ensure it's above canvas content, but below modals if any
              minWidth: '120px' // Ensure it has some width for sliders
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12}>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <ZoomInIcon sx={{ color: 'white', fontSize: '1rem' }} />
                  <Typography gutterBottom sx={{ color: 'white', fontSize: '0.8rem', m:0 }}>Scale: {Math.round(silhouetteScale * 100)}%</Typography>
                </Stack>
                <Slider
                  size="small"
                  aria-label="Scale"
                  value={silhouetteScale}
                  onChange={(e, newValue) => setSilhouetteScale(newValue)}
                  min={0.1}
                  max={3}
                  step={0.05}
                  valueLabelDisplay="auto"
                  sx={{ color: 'white', '& .MuiSlider-thumb': { backgroundColor: 'white' }, '& .MuiSlider-rail': { opacity: 0.4 }, '& .MuiSlider-track': { opacity: 0.8 } }}
                />
              </Grid>
              <Grid item xs={12}>
                 <Stack spacing={0.5} direction="row" alignItems="center">
                  <OpacityIcon sx={{ color: 'white', fontSize: '1rem' }} />
                  <Typography gutterBottom sx={{ color: 'white', fontSize: '0.8rem', m:0 }}>Opacity: {Math.round(silhouetteOpacity * 100)}%</Typography>
                </Stack>
                <Slider
                  size="small"
                  aria-label="Opacity"
                  value={silhouetteOpacity}
                  onChange={(e, newValue) => setSilhouetteOpacity(newValue)}
                  min={0}
                  max={1}
                  step={0.05}
                  valueLabelDisplay="auto"
                  sx={{ color: 'white', '& .MuiSlider-thumb': { backgroundColor: 'white' }, '& .MuiSlider-rail': { opacity: 0.4 }, '& .MuiSlider-track': { opacity: 0.8 } }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Overlay for Silhouette Selector */}
        {!isCameraActive && backgroundImage && (
          <Box 
            sx={{
              position: 'absolute',
              bottom: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              p: 1.5,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              zIndex: 20,
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center'
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <IconButton 
                onClick={handlePrevSilhouettes} 
                disabled={currentSilhouetteStartIndex === 0}
                sx={{ color: 'white' }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>

              {silhouettes.slice(currentSilhouetteStartIndex, currentSilhouetteStartIndex + silhouettesPerPage).map(sil => {
                const locked = sil.pro && !features.allSilhouettes;
                return (
                  <Box
                    key={sil.name}
                    onClick={() => handleSilhouetteSelect(sil)}
                    sx={{
                      position: 'relative',
                      width: 60, // Slightly smaller for overlay
                      height: 60, // Slightly smaller for overlay
                      backgroundImage: `url(${sil.src})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      backgroundColor: silhouetteImage === sil.src ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
                      border: silhouetteImage === sil.src ? '2px solid white' : '2px solid transparent',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      m: 0.5,
                      opacity: locked ? 0.55 : 1,
                      transition: 'background-color 0.3s, border-color 0.3s',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.3)',
                      }
                    }}
                  >
                    {locked && (
                      <LockIcon
                        sx={{
                          position: 'absolute', top: '50%', left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: 'white', fontSize: '1.4rem',
                          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
                        }}
                      />
                    )}
                  </Box>
                );
              })}
              {/* Custom silhouette upload tile (Pro-only) */}
              <Box
                onClick={handleCustomSilhouetteClick}
                aria-label="upload custom silhouette"
                sx={{
                  position: 'relative',
                  width: 60,
                  height: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  border: '2px dashed rgba(255,255,255,0.6)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  m: 0.5,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                }}
              >
                <AddPhotoAlternateIcon sx={{ color: 'white' }} />
                {!features.customUpload && (
                  <LockIcon
                    sx={{
                      position: 'absolute', top: 2, right: 2,
                      color: 'white', fontSize: '0.9rem',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
                    }}
                  />
                )}
              </Box>
              <IconButton
                onClick={handleNextSilhouettes} 
                disabled={currentSilhouetteStartIndex >= silhouettes.length - silhouettesPerPage}
                sx={{ color: 'white' }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Stack>
          </Box>
        )}

        {/* Overlay for Download Button */}
        {!isCameraActive && backgroundImage && (
          <>
            <IconButton
              aria-label="download image"
              onClick={handleDownloadImage}
              disabled={!backgroundImage}
              sx={{
                position: 'absolute',
                top: '10px', // Reverted to 10px as import button is not shown when !isCameraActive
                left: '10px',
                zIndex: 20,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.3)'
                },
                p: '8px'
              }}
            >
              <img src="/img/Save.png" alt="Save" style={{ width: 24, height: 24 }} />
            </IconButton>
          </>
        )}
      </Box>

      {/* AdSense Ad Unit — hidden for Pro (removeAds) users */}
      {!features.removeAds && (
        <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
          <AdsenseAd
            publisherId="ca-pub-2352940492649564"
            adSlot="7802069983"
          />
        </Box>
      )}

      {/* Pro upgrade modal */}
      <ProUpgradeModal
        open={showProModal}
        onClose={() => setShowProModal(false)}
        startCheckout={startCheckout}
      />

      {/* Post-purchase confirmation */}
      <Snackbar
        open={justUpgraded}
        autoHideDuration={6000}
        onClose={dismissUpgraded}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={dismissUpgraded} severity="success" sx={{ width: '100%' }}>
          You're Pro now — thanks for the support! 🎉
        </Alert>
      </Snackbar>

    </Container>
  );
}

export default AppPage; 